import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Users, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Empty } from "@/components/common";
import { WidgetPicker } from "@/components/WidgetPicker";
import { RegistrationsPanel, RegLinksButton } from "@/components/Registrations";
import { AdjustmentsSection } from "@/components/Adjustments";
import { OrgCode } from "@/components/OrgCode";
import { DeleteUserButton } from "@/components/DeleteUser";
import { ManagerFields, LocationField } from "@/components/ManagerFields";
import { ROLE_LABEL } from "@/lib/perm";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const TEXT = [["name", "Nome e cognome"], ["email", "Email"], ["job_title", "Mansione"], ["department", "Reparto"]];

function UserDialog({ user, partners, onClose, onDone }) {
  const { settings } = useAuth();
  const defaultWidgets = settings?.employee_dashboard_widgets;
  const isNew = !user.id;
  const fields = useMemo(() => TEXT.filter(([k]) => isNew || k !== "email"), [isNew]);
  const referents = useMemo(() => partners.filter((p) => p.id !== user.id), [partners, user.id]);
  const [f, setF] = useState({ role: "employee", password: "", send_invite: true, permissions: [], manager_location: "", ...user, annual_leave_days: user.annual_leave_days ?? "", annual_permission_hours: user.annual_permission_hours ?? "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const resend = async () => {
    try { await api.post(`/admin/users/${user.id}/invite`); toast.success(`Invito inviato a ${user.email}`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async (e) => {
    e.preventDefault();
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      name: f.name, role: f.role, department: f.department || "", location: f.location || "", job_title: f.job_title || "",
      annual_leave_days: num(f.annual_leave_days), annual_permission_hours: num(f.annual_permission_hours), is_partner: !!f.is_partner,
      permissions: f.role === "manager" ? f.permissions || [] : [], manager_location: f.role === "manager" && f.manager_location ? f.manager_location : null,
    };
    try {
      if (isNew) {
        const { data } = await api.post("/admin/users", { ...payload, email: f.email, send_invite: f.send_invite, password: f.send_invite ? null : f.password });
        if (f.send_invite) data.invite_sent ? toast.success(`Invito inviato a ${data.email}`) : toast.error("Dipendente creato, ma l'invio dell'invito non è riuscito. Riprova da 'Reinvia invito'.");
      } else await api.put(`/admin/users/${user.id}`, { ...payload, active: f.active, dashboard_widgets: f.dashboard_widgets ?? null, reference_partner_id: f.reference_partner_id && f.reference_partner_id !== "none" ? f.reference_partner_id : null, ...(f.password ? { password: f.password } : {}) });
      toast.success(isNew ? "Dipendente creato" : "Dati aggiornati");
      onClose();
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nuovo dipendente" : user.name}</DialogTitle>
          <DialogDescription>{isNew ? "Invia un invito via email: il dipendente sceglierà da solo la propria password." : user.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type={k === "email" ? "email" : "text"} value={f[k] || ""} onChange={set(k)} required={k === "name" || k === "email"} data-testid={`user-${k}-input`} /></div>
            ))}
            <div className="space-y-1.5">
              <Label>Ruolo</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="employee">Dipendente</SelectItem><SelectItem value="manager">Responsabile</SelectItem><SelectItem value="admin">Amministratore</SelectItem></SelectContent>
              </Select>
            </div>
            <LocationField value={f.location || ""} onChange={(v) => setF({ ...f, location: v })} />
            <div className="space-y-1.5"><Label>Giorni ferie annui</Label><Input type="number" min={0} placeholder="Predefinito" value={f.annual_leave_days} onChange={set("annual_leave_days")} data-testid="user-leave-days-input" /></div>
            <div className="space-y-1.5"><Label>Ore permesso annue</Label><Input type="number" min={0} placeholder="Predefinito" value={f.annual_permission_hours} onChange={set("annual_permission_hours")} data-testid="user-permission-hours-input" /></div>
          </div>
          {f.role === "manager" && <ManagerFields f={f} setF={setF} />}
          {isNew && (
            <label className="flex items-center justify-between rounded-md border border-primary/40 bg-primary/5 px-3 py-2.5 text-sm">
              <span><span className="flex items-center gap-1.5 font-medium"><Mail className="h-4 w-4 text-primary" />Invia invito via email</span><span className="text-xs text-muted-foreground">Email di benvenuto con link per scegliere la password (valido 72 ore)</span></span>
              <Switch checked={f.send_invite} onCheckedChange={(c) => setF({ ...f, send_invite: c })} data-testid="user-send-invite-switch" />
            </label>
          )}
          {!(isNew && f.send_invite) && (
            <div className="space-y-1.5">
              <Label>{isNew ? "Password iniziale (min. 8)" : "Nuova password (lascia vuoto per non cambiarla)"}</Label>
              <Input type="text" value={f.password} onChange={set("password")} required={isNew} minLength={8} data-testid="user-password-input" />
            </div>
          )}
          {!isNew && (
            <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-sm">
              <span>{user.invite_pending ? <span className="font-medium text-amber-700 dark:text-amber-300" data-testid="user-invite-pending">Invito in attesa di attivazione</span> : "Invito di accesso"}
                {user.invite_sent_at && <span className="block text-xs text-muted-foreground">Ultimo invio {fmtDateTime(user.invite_sent_at)}</span>}</span>
              <Button type="button" size="sm" variant="outline" onClick={resend} data-testid="user-resend-invite-button"><Send className="mr-1.5 h-3.5 w-3.5" />{user.invite_pending ? "Reinvia invito" : "Invia nuovo invito"}</Button>
            </div>
          )}
          <label className="flex items-center justify-between rounded-md border px-3 py-2.5 text-sm">
            <span><span className="block">Socio</span><span className="text-xs text-muted-foreground">Può ricevere richieste di incontro</span></span>
            <Switch checked={!!f.is_partner} onCheckedChange={(c) => setF({ ...f, is_partner: c })} data-testid="user-partner-switch" />
          </label>
          {!isNew && <AdjustmentsSection user={user} onDone={onDone} />}
          {!isNew && f.role === "employee" && (
            <div className="space-y-3 rounded-md border p-3">
              <label className="flex items-center justify-between text-sm">
                <span><span className="block">Dashboard personalizzata</span><span className="text-xs text-muted-foreground">Altrimenti usa l'impostazione aziendale</span></span>
                <Switch checked={Array.isArray(f.dashboard_widgets)} onCheckedChange={(c) => setF({ ...f, dashboard_widgets: c ? [...(defaultWidgets || [])] : null })} data-testid="user-custom-dashboard-switch" />
              </label>
              {Array.isArray(f.dashboard_widgets) && <WidgetPicker prefix="user" value={f.dashboard_widgets} onChange={(v) => setF({ ...f, dashboard_widgets: v })} />}
            </div>
          )}
          {!isNew && (
            <div className="space-y-1.5">
              <Label>Referente (socio o amministratore)</Label>
              <Select value={f.reference_partner_id || "none"} onValueChange={(v) => setF({ ...f, reference_partner_id: v })}>
                <SelectTrigger data-testid="user-reference-partner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessuno</SelectItem>
                  {referents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.role === "admin" ? " · Amministratore" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isNew && (
            <label className="flex items-center justify-between rounded-md border px-3 py-2.5 text-sm">Account attivo<Switch checked={f.active} onCheckedChange={(c) => setF({ ...f, active: c })} data-testid="user-active-switch" /></label>
          )}
          <DialogFooter><Button type="submit" data-testid="user-submit-button">{isNew ? "Crea" : "Salva"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Dipendenti() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => users.filter((u) => `${u.name} ${u.email} ${u.department}`.toLowerCase().includes(q.toLowerCase())), [users, q]);
  const partners = useMemo(() => users.filter((u) => (u.is_partner || u.role === "admin") && u.active), [users]);

  return (
    <div data-testid="employees-page">
      <PageHeader title="Dipendenti" subtitle={`${users.filter((u) => u.active).length} account attivi`}
        actions={<><RegLinksButton /><Button onClick={() => setEdit({})} data-testid="new-employee-button"><Plus className="mr-1.5 h-4 w-4" />Nuovo dipendente</Button></>} />
      <OrgCode compact />
      <RegistrationsPanel onChange={load} />
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Cerca per nome, email, reparto" value={q} onChange={(e) => setQ(e.target.value)} data-testid="employees-search-input" />
      </div>
      {shown.length === 0 ? <Empty icon={Users} text="Nessun dipendente trovato." /> : (
        <Panel className="overflow-x-auto fade-up">
          <Table data-testid="employees-table">
            <TableHeader>
              <TableRow><TableHead>Nome</TableHead><TableHead>Ruolo</TableHead><TableHead>Reparto / Sede</TableHead><TableHead>Ferie residue</TableHead><TableHead>Permessi residui</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((u) => (
                <TableRow key={u.id} className="cursor-pointer" onClick={() => setEdit(u)} data-testid={`employee-row-${u.id}`}>
                  <TableCell><span className="font-medium">{u.name}</span><span className="block text-xs text-muted-foreground">{u.email}</span></TableCell>
                  <TableCell>{ROLE_LABEL[u.role] || "Dipendente"}{u.is_partner && <span className="ml-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary" data-testid={`employee-partner-badge-${u.id}`}>Socio</span>}</TableCell>
                  <TableCell className="text-sm">{u.department || "—"}<span className="block text-xs text-muted-foreground">{u.location}</span></TableCell>
                  <TableCell>{u.balance.leave_remaining} / {u.balance.leave_total} gg</TableCell>
                  <TableCell>{u.balance.permission_remaining} / {u.balance.permission_total} h</TableCell>
                  <TableCell>{!u.active ? <span className="text-xs text-muted-foreground">Disattivato</span> : u.invite_pending ? <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Invito in attesa</span> : <span className="text-xs font-medium text-primary">Attivo</span>}</TableCell>
                  <TableCell className="text-right">{u.id !== me?.id && <DeleteUserButton user={u} onDone={load} />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      )}
      {edit && <UserDialog user={edit} partners={partners} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}
